import moment from 'moment';
import { resolveSeriesPeriod } from '../chart/customChartModel.js';
import { GROUPS } from './summaryCompareCatalog.js';
import { MAX_COMPARISONS } from './summaryCompareQuery.js';

const MINUTE = 60000;
const LENGTH_TOLERANCE = MINUTE;
const MIN_COLUMNS = 4;

export const MAX_COLUMNS = MAX_COMPARISONS + 1;
export const COLUMN_LETTERS = Array.from({ length: MAX_COLUMNS }, (_, i) => String.fromCharCode(65 + i));

// label 280 + Base 130 + tiap pembanding (angka 130 + delta 100)
export function requiredTableWidth(n) {
  return 280 + 130 + (n - 1) * 230;
}

// kolom yang muat di lebar tabel, dipatok MIN_COLUMNS..MAX_COLUMNS
export function columnsThatFit(availableWidth) {
  const width = Number(availableWidth);
  const fit = Number.isFinite(width) ? Math.floor((width - requiredTableWidth(1)) / 230) + 1 : 0;
  return Math.min(MAX_COLUMNS, Math.max(MIN_COLUMNS, fit));
}

const GROUP_ORDER = {};
GROUPS.forEach((g, i) => { GROUP_ORDER[g.key] = i; });

// `custom` tanggalnya apa adanya; sisanya dihitung relatif kayak Custom Chart.
export function resolveRightRange(period, mainRange) {
  if (period && period.kind === 'custom') {
    return { startMs: Number(period.startMs), endMs: Number(period.endMs) };
  }
  const resolved = resolveSeriesPeriod(period, mainRange);
  return { startMs: resolved.startMs, endMs: resolved.endMs };
}

export function rangeText(range) {
  const start = moment(range.startMs);
  const end = moment(range.endMs);
  // Selalu tampilin jam biar rentang gak ambigu; tanggal doang cuma kalau start & end sehari sama.
  if (start.isSame(end, 'day')) {
    return `${start.format('DD MMM YYYY HH:mm')} – ${end.format('HH:mm')}`;
  }
  return `${start.format('DD MMM YYYY HH:mm')} – ${end.format('DD MMM YYYY HH:mm')}`;
}

export function lengthText(range) {
  const ms = Math.max(range.endMs - range.startMs, 0);
  const days = ms / 86400000;
  if (days >= 1) return `${Math.round(days)} day${Math.round(days) === 1 ? '' : 's'}`;
  const hours = ms / 3600000;
  if (hours >= 1) return `${Math.round(hours)} hour${Math.round(hours) === 1 ? '' : 's'}`;
  const minutes = Math.round(ms / MINUTE);
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

export function lengthsDiffer(a, b) {
  if (!a || !b) return false;
  return Math.abs((a.endMs - a.startMs) - (b.endMs - b.startMs)) > LENGTH_TOLERANCE;
}

export function sameSide(left, right) {
  if (!left || !right) return false;
  return String(left.massId) === String(right.massId)
    && Math.abs(left.range.startMs - right.range.startMs) <= LENGTH_TOLERANCE
    && Math.abs(left.range.endMs - right.range.endMs) <= LENGTH_TOLERANCE;
}

export function requestFor(massId, range, nowMs) {
  const startSec = Math.floor(range.startMs / 1000);
  let endSec = Math.floor(range.endMs / 1000);
  if (Number.isFinite(nowMs)) endSec = Math.min(endSec, Math.floor(nowMs / 1000));
  return {
    key: `${massId}|${startSec}|${endSec}`, massId, startSec, endSec,
  };
}

export function deltaPct(left, right) {
  if (!Number.isFinite(left) || !Number.isFinite(right) || left === 0) return null;
  return ((right - left) / left) * 100;
}

// Durasi ringkas biar kolom angka tetap sempit ("7d 18h 4m").
export function shortDuration(seconds) {
  if (seconds === null || !Number.isFinite(Number(seconds))) return '—';
  const total = Math.max(0, Math.floor(Number(seconds)));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

export function deltaText(pct) {
  if (!Number.isFinite(pct)) return '—';
  const fixed = pct.toFixed(1);
  // -0.04% kebulat jadi "-0.0" -- minus di depan nol kebaca kayak rusak.
  if (Number(fixed) === 0) return '0.0%';
  return `${pct > 0 ? '+' : ''}${fixed}%`;
}

function splitIntoChunks(value) {
  return value.match(/\d+|\D+/g) || [];
}

// Natural order (AE2 < AE10), bukan localeCompare yang naruh AE_INLINE sebelum AE4.
function compareNames(a, b) {
  if (a === b) return 0;
  const chunksA = splitIntoChunks(a);
  const chunksB = splitIntoChunks(b);
  const len = Math.max(chunksA.length, chunksB.length);
  const diffs = Array.from({ length: len }, (_, i) => {
    const ca = chunksA[i] || '';
    const cb = chunksB[i] || '';
    if (/^\d+$/.test(ca) && /^\d+$/.test(cb)) return Number(ca) - Number(cb);
    if (ca === cb) return 0;
    return ca < cb ? -1 : 1;
  });
  return diffs.find((d) => d !== 0) || 0;
}

// grup > subgroup > metrik > channel > arah
function compareRows(a, b) {
  const groupCmp = GROUP_ORDER[a.group] - GROUP_ORDER[b.group];
  if (groupCmp !== 0) return groupCmp;
  if (a.subgroupRank !== b.subgroupRank) return a.subgroupRank - b.subgroupRank;
  const subgroupCmp = compareNames(a.subgroup || '', b.subgroup || '');
  if (subgroupCmp !== 0) return subgroupCmp;
  if (a.metricIndex !== b.metricIndex) return a.metricIndex - b.metricIndex;
  if (a.channelRank !== b.channelRank) return a.channelRank - b.channelRank;
  const nameCmp = compareNames(a.channelName || '', b.channelName || '');
  if (nameCmp !== 0) return nameCmp;
  return a.direction - b.direction;
}

// index 0 = Base; kolom null (kartu gagal load) diperlakukan kosong.
export function mergeRows(...columnsRows) {
  const columnCount = columnsRows.length;
  const merged = new Map();
  const put = (row, colIndex) => {
    const existing = merged.get(row.key) || {
      key: row.key,
      group: row.group,
      label: row.label,
      unit: row.unit,
      format: row.format,
      measure: row.measure,
      order: row.order,
      metricIndex: row.metricIndex,
      channelRank: row.channelRank,
      channelName: row.channelName,
      direction: row.direction,
      subgroup: row.subgroup,
      subgroupRank: row.subgroupRank,
      values: new Array(columnCount).fill(null),
    };
    existing.values[colIndex] = Number.isFinite(row.value) ? row.value : null;
    merged.set(row.key, existing);
  };
  columnsRows.forEach((rows, colIndex) => {
    (rows || []).forEach((row) => put(row, colIndex));
  });
  return Array.from(merged.values())
    .filter((row) => row.values.some((v) => v !== null))
    .map((row) => ({ ...row, pcts: row.values.map((v, i) => (i === 0 ? null : deltaPct(row.values[0], v))) }))
    .sort(compareRows);
}

// expectedKeys: grup tetap keluar (rows: []) kalau key-nya diharapkan walau kosong.
export function groupRows(mergedRows, groups, expectedKeys) {
  const expected = expectedKeys ? new Set(expectedKeys) : null;
  return groups
    .map((group) => ({
      key: group.key,
      label: group.label,
      rows: mergedRows.filter((row) => row.group === group.key),
    }))
    .filter((group) => group.rows.length > 0 || (expected && expected.has(group.key)));
}
